import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  CONTACT_EMAIL,
  PHONE_KUWAIT,
  PHONE_UAE,
  WHATSAPP_URL,
} from "../../utils/contactInfo";
import "../../styles/jg.css";

/* "jamil" — beautiful — the studio's namesake, used across the design */
export const AR_JAMIL = "جميل";

const NAV_LINKS = [
  { idx: "01", label: "Services", to: "/what-we-do" },
  { idx: "02", label: "Work", to: "/case-studies" },
  { idx: "03", label: "Products", to: "/products" },
  { idx: "04", label: "Blog", to: "/blog" },
  { idx: "05", label: "About", to: "/about" },
];

/**
 * Re-runs the reveal observer whenever `deps` change so route content
 * animates in. Elements tagged .jg-reveal fade/rise on first view.
 */
export const useJgReveal = (deps = []) => {
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-in");
            io.unobserve(entry.target);
          }
        });
      },
      // Fire just before elements enter the viewport so fast scrolls
      // never land on a blank section.
      { threshold: 0, rootMargin: "0px 0px 15% 0px" }
    );

    const observeAll = () => {
      document.querySelectorAll(".jg-reveal:not(.is-in)").forEach((el) => {
        // Elements already on screen reveal instantly — don't gate first
        // paint on observer timing (throttled tabs, prerender, etc.).
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight * 1.15 && rect.bottom > 0) {
          el.classList.add("is-in");
        } else {
          io.observe(el);
        }
      });
    };

    observeAll();

    // Async content (lazy routes, fetched data) adds .jg-reveal elements
    // after mount; watch the DOM so they get observed too. Batched to one
    // pass per frame so bursts of mutations don't force repeated layouts.
    let moRaf = 0;
    const mo = new MutationObserver(() => {
      if (moRaf) return;
      moRaf = requestAnimationFrame(() => {
        moRaf = 0;
        observeAll();
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });

    // Fallback for tabs where IntersectionObserver is throttled
    // (hidden/occluded). Runs once after scrolling settles instead of on
    // every frame, so it never forces layout mid-scroll.
    let idle = 0;
    const onScroll = () => {
      clearTimeout(idle);
      idle = setTimeout(observeAll, 150);
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(idle);
      if (moRaf) cancelAnimationFrame(moRaf);
      mo.disconnect();
      io.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

export const JgHeader = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <>
      <header className="jg-header">
        <div className="jg-wrap jg-header-inner">
          <Link to="/" className="jg-wordmark">
            <span className="ar" aria-hidden="true">
              {AR_JAMIL}
            </span>
            Jamil Global
          </Link>
          <nav className="jg-nav" aria-label="Primary">
            {NAV_LINKS.slice(0, 4).map((l) => (
              <Link key={l.to} to={l.to}>
                {l.label}
              </Link>
            ))}
            <Link to="/contact-us" className="jg-nav-cta">
              <span className="jg-nav-cta-long">Start a project</span>
              <span className="jg-nav-cta-short">Start</span>
            </Link>
            <button
              type="button"
              className="jg-burger"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              aria-expanded={menuOpen}
            >
              Menu
            </button>
          </nav>
        </div>
      </header>

      {menuOpen && (
        <div className="jg-menu" role="dialog" aria-label="Site menu">
          <button
            type="button"
            className="jg-menu-close"
            onClick={() => setMenuOpen(false)}
          >
            Close ✕
          </button>
          <nav aria-label="Mobile">
            {NAV_LINKS.map((l) => (
              <Link key={l.to} to={l.to}>
                {l.label}
                <span className="idx">{l.idx}</span>
              </Link>
            ))}
            <Link to="/contact-us">
              Start a project
              <span className="idx">→</span>
            </Link>
          </nav>
          <div className="jg-menu-foot">
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            <a href={`tel:${PHONE_KUWAIT.tel}`}>{PHONE_KUWAIT.display}</a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              WhatsApp {PHONE_UAE.display}
            </a>
            <span>Kuwait City → GCC</span>
          </div>
        </div>
      )}
    </>
  );
};

export const JgFooter = () => (
  <footer className="jg-footer">
    <div className="jg-wrap">
      <div className="jg-footer-grid">
        <div className="jg-footer-col">
          <span className="jg-label">Studio</span>
          <ul>
            <li>
              <Link to="/about">About</Link>
            </li>
            <li>
              <Link to="/what-we-do">What we do</Link>
            </li>
            <li>
              <Link to="/custom-solutions">Custom solutions</Link>
            </li>
            <li>
              <Link to="/ai-in-kuwait">AI in Kuwait</Link>
            </li>
            <li>
              <Link to="/research/gulf-digital-marketing-statistics">Gulf statistics</Link>
            </li>
          </ul>
        </div>
        <div className="jg-footer-col">
          <span className="jg-label">Work</span>
          <ul>
            <li>
              <Link to="/case-studies">Case studies</Link>
            </li>
            <li>
              <Link to="/products">Products</Link>
            </li>
            <li>
              <Link to="/blog">Blog</Link>
            </li>
            <li>
              <Link to="/tools/ad-budget-calculator">Ad budget calculator</Link>
            </li>
          </ul>
        </div>
        <div className="jg-footer-col">
          <span className="jg-label">Contact</span>
          <ul>
            <li>
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            </li>
            <li>
              <a href={`tel:${PHONE_KUWAIT.tel}`}>
                Kuwait {PHONE_KUWAIT.display}
              </a>
            </li>
            <li>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                WhatsApp {PHONE_UAE.display}
              </a>
            </li>
            <li>
              <Link to="/contact-us">Contact form</Link>
            </li>
            <li>
              <Link to="/privacy">Privacy</Link>
            </li>
            <li>
              <Link to="/terms">Terms</Link>
            </li>
          </ul>
        </div>
      </div>
      <div className="jg-footer-bottom">
        <span>© {new Date().getFullYear()} Jamil Global</span>
        <span>
          <Link to="/privacy">Privacy</Link>
          {" · "}
          <Link to="/terms">Terms</Link>
        </span>
        <span>Kuwait City → GCC</span>
        <span>{AR_JAMIL} — beautiful, by design</span>
      </div>
    </div>
  </footer>
);

/**
 * Page chrome for every page: paper background, fixed header, footer,
 * scroll-reveal wiring, and scroll-to-top on mount.
 */
const JgShell = ({ children, footer = true }) => {
  const location = useLocation();
  useJgReveal([location.pathname]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="jg">
      <JgHeader />
      <main>{children}</main>
      {footer && <JgFooter />}
    </div>
  );
};

export default JgShell;
